import { Routes } from '@angular/router';
import { EnhancedAuthGuard } from 'src/app/core/guards/enhanced-auth.guard';
import { preserveParamsRedirect } from 'src/app/core/routing/angular-redirects.routes';
import { HelpId } from 'src/app/core/routing/help-id';
import { RouteData } from 'src/app/core/models/route-data';

/**
 * Workflow routes grouped here because they all use the multi-outlet pattern
 * (MultiOutletContainerComponent with header, sideRail, content, footer outlets)
 * and share workflow-specific side rails and headers.
 */
export const WORKFLOW_ROUTES: Routes = [
    // In the original UI-Router, `workflow` (url /workflow) was the LAYOUT PARENT and the real
    // destination was its child `interview` (url /workflow/interview) - every navigation went to
    // 'interview', never bare 'workflow', and users always saw /workflow/interview. The initial
    // migration inverted this (made /workflow the content route and dropped the child). We restore
    // the original: /workflow/interview is the canonical route (below); bare /workflow just
    // redirects there, preserving query params, so any stale link still resolves.
    // (see QUERY_PARAM_SERIALIZATION_REGRESSIONS.md)
    {
        path: 'workflow',
        pathMatch: 'full',
        redirectTo: preserveParamsRedirect('/workflow/interview')
    },

    // Original UI-Router child state `interview` (parent: 'workflow', url: '/interview') =>
    // full path `/workflow/interview`. SHUI deep-links to this exact URL. WorkflowComponent
    // renders interview content purely from the `interviewId` query param.
    {
        path: 'workflow/interview',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false,
            helpId: {
                advisors: 7,
                // Contextual variants keyed by the last 3 chars of the `subTaskType` query param.
                // Preserves pre-FOF-7854 behaviour of determineHelpPathForAdvisors, which branched
                // on subTaskType.slice(-3) for ACQ and G&E interviews. See FOF-7854.
                bySubTaskType: { acq: { advisors: 40 }, gne: { advisors: 46 } }
            } satisfies HelpId
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/workflow/workflow.component').then(m => m.WorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/side-rail.component').then(m => m.WorkflowSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'workflows',
        loadComponent: () => import('src/components/all-workflows/all-workflows.component').then(m => m.AllWorkflowsComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'workflowView',
            requiresPermission: true
        } satisfies RouteData
    },

    {
        path: 'filingWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'filingWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/workflow/filing/filing-workflow.component').then(m => m.FilingWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/side-rail.component').then(m => m.WorkflowSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'filingWorkflowMultipleDisclosures',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'filingWorkflowMultipleDisclosures',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/workflow/filing/multiple-disclosures/filing-workflow-multiple-disclosures.component').then(m => m.FilingWorkflowMultipleDisclosuresComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/side-rail.component').then(m => m.WorkflowSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'addInsuranceWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'addInsuranceWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/add-insurance-workflow/add-insurance-workflow.component').then(m => m.AddInsuranceWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'connectionWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'connectionWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/connection/connection-workflow/connection-workflow.component').then(m => m.ConnectionWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'carrierConnectWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'carrierConnectWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/carrier-connect-workflow/carrier-connect-workflow.component').then(m => m.CarrierConnectWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'producerContractWorkflow/create',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'createProducerContractWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/producer-contracts/producer-contract-workflow/create-producer-contract-workflow/create-producer-contract-workflow.component').then(m => m.CreateProducerContractWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/contract-configuration-workflow-side-rail/contract-configuration-workflow-side-rail.component').then(m => m.ContractConfigurationWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'producerContractWorkflow/update',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/contract-configuration-workflow-side-rail/contract-configuration-workflow-side-rail.component').then(m => m.ContractConfigurationWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'manageContractConfigVersionWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'manageContractConfigVersionWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/contract-configuration-workflow/manage-contract-config-version-workflow/manage-contract-config-version-workflow.component').then(m => m.ManageContractConfigVersionWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/contract-configuration-workflow-side-rail/contract-configuration-workflow-side-rail.component').then(m => m.ContractConfigurationWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'createContractConfigurationVersion',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'createContractVersionWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/contract-configuration-workflow/create-contract-config-version-workflow/create-contract-config-version-workflow.component').then(m => m.CreateContractConfigVersionWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/contract-configuration-workflow-side-rail/contract-configuration-workflow-side-rail.component').then(m => m.ContractConfigurationWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'createMinorContractConfigurationVersion',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'createMinorVersionWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/contract-configuration-workflow/create-minor-contract-config-version-workflow/create-minor-contract-config-version-workflow.component').then(m => m.CreateMinorContractConfigVersionWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/contract-configuration-workflow-side-rail/contract-configuration-workflow-side-rail.component').then(m => m.ContractConfigurationWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'carrierContractWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'carrierContractWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/carrier-contract-workflow/carrier-contract-workflow.component').then(m => m.CarrierContractWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'docusignTemplateWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/contract-documents/docusign-template-manager/docusign-template-manager.component').then(m => m.DocusignTemplateManagerComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/contract-documents/docusign-template-manager/docusign-template-workflow-side-rail/docusign-template-workflow-side-rail.component').then(m => m.DocusignTemplateWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'contractConfigurationWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                // Global masthead now renders at the app level (removed from usesCustomHeader in
                // app.component.ts). This outlet stays empty so no blank duplicate renders inside
                // MultiOutletContainerComponent. See FOF-7857.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/contract-configuration-workflow/contract-configuration-workflow.component').then(m => m.ContractConfigurationWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/contract-configuration-workflow-side-rail/contract-configuration-workflow-side-rail.component').then(m => m.ContractConfigurationWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'modifyProducerTerminations',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/producer-termination/modify-producer-terminations-workflow/modify-producer-terminations-workflow.component').then(m => m.ModifyProducerTerminationsWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/producer-termination/modify-producer-terminations-workflow/modify-producer-terminations-side-rail/modify-producer-terminations-side-rail.component').then(m => m.ModifyProducerTerminationsSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
        ]
    },

    {
        path: 'manageSellingRelationshipTerminationPeriods',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/selling-relationship/manage-termination-periods-workflow/manage-termination-periods-workflow.component').then(m => m.ManageTerminationPeriodsWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/selling-relationship/manage-termination-periods-workflow/manage-termination-periods-side-rail/manage-termination-periods-side-rail.component').then(m => m.ManageTerminationPeriodsSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
        ]
    },

    {
        path: 'producerCompScheduleWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'producerCompScheduleWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/producer-comp-schedule-workflow/producer-comp-schedule-workflow.component').then(m => m.ProducerCompScheduleWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/contract-configuration-workflow-side-rail/contract-configuration-workflow-side-rail.component').then(m => m.ContractConfigurationWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'compScheduleConfigurationWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'compScheduleConfigurationWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/comp-schedule-configuration-workflow/comp-schedule-configuration-workflow.component').then(m => m.CompScheduleConfigurationWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/contract-configuration-workflow-side-rail/contract-configuration-workflow-side-rail.component').then(m => m.ContractConfigurationWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'manageProducerWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'manageProducerWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/producer-workflow-config/manage-producer-workflow-config/manage-producer-workflow-config.component').then(m => m.ManageProducerWorkflowConfigComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'manageBeneficiariesWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/producer-beneficiaries/manage-beneficiaries-workflow/manage-beneficiaries-workflow.component').then(m => m.ManageBeneficiariesWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/producer-beneficiaries/beneficiary-workflow-side-rail/beneficiary-workflow-side-rail.component').then(m => m.BeneficiaryWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'addPaymentPreference',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/personal-summary/payment-preferences/add-payment-preference/add-payment-preference.component').then(m => m.AddPaymentPreferenceComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/personal-summary/payment-preferences/add-payment-preference/payment-preference-workflow-side-rail/payment-preference-workflow-side-rail.component').then(m => m.PaymentPreferenceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'subscriptionUpgradeWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'subscriptionUpgradeWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/subscription-upgrade-workflow/subscription-upgrade-workflow.component').then(m => m.SubscriptionUpgradeWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'agencySubscriptionWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'agencySubscriptionWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/agency-subscription-workflow/agency-subscription-workflow.component').then(m => m.AgencySubscriptionWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    // Note: These are child routes under 'admin' parent in UI-Router
    // They will need to be nested under the admin route when it's migrated
    {
        path: 'admin/editWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'adminEditWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/workflow/configuration/edit-workflow/edit-workflow.component').then(m => m.EditWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/configuration/edit-workflow-side-rail/edit-workflow-side-rail.component').then(m => m.EditWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    // NOTE: This route moved to misc.routes.ts as a child of the /admin route
    // to properly inherit the admin layout (standard side rail + TopNav with admin tabs)
    // See Issue #34 - Configure Workflows was showing license print side rail instead of admin layout

    {
        path: 'consoleWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'consoleWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/configuration-console/console-workflow/console-workflow.component').then(m => m.ConsoleWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/configuration-console/configuration-console-workflow-side-rail.component').then(m => m.ConfigurationConsoleWorkflowSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    // Note: accountRecoveryWorkflow (unauthenticated) has been moved to WORKFLOW_PUBLIC_ROUTES
    // and is imported at root level in app.routes.ts
    //
    // Note: signInHelpWorkflow (unauthenticated) is already at root level in app.routes.ts
    // and has been removed from this array to avoid duplication

    {
        path: 'configureQuestionnaireWorkflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/configure-custom-questionnaire/configure-custom-questionnaire.component').then(m => m.ConfigureCustomQuestionnaireComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'bulkRequestQuestionnaireResponses',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/bulk-request-questionnaire-responses/bulk-request-questionnaire-responses.component').then(m => m.BulkRequestQuestionnaireResponsesComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'modifyHierarchyNodeHistoricalPeriods',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/hierarchy/modify-historical-periods/modify-historical-periods.component').then(m => m.ModifyHistoricalPeriodsComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'transfer-license-status',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/transfer-license-status/transfer-license-status-workflow.component').then(m => m.TransferLicenseStatusWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/transfer-license-status/siderail/transfer-license-status-side-rail.component').then(m => m.TransferLicenseStatusSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'ce-transcript',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'ceTranscript',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/ce-transcript/workflow/ce-transcript-workflow.component').then(m => m.CeTranscriptWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/ce-transcript/workflow/siderail/ce-transcript-side-rail.component').then(m => m.CeTranscriptSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    // Producer Interview Workflow
    {
        path: 'producer-interview-workflow',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'producerInterviewWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/producer-interview-workflow/producer-interview-workflow.component').then(m => m.ProducerInterviewWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/producer-interview-workflow/producer-interview-workflow-side-rail/producer-interview-workflow-side-rail.component').then(m => m.ProducerInterviewWorkflowSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    // Producer Interview Contract Signing — separate route so the producer interview workflow side rail is not shown
    {
        path: 'producer-interview-contract-signing',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'producerInterviewWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/producer-interview-workflow/contract-signing/contract-signing.component').then(m => m.ProducerInterviewContractSigningComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    }
];

/**
 * Public (Unauthenticated) Workflow Routes
 *
 * These routes must be at root level in app.routes.ts, not nested in protected parent.
 * Includes account recovery workflow that doesn't require authentication.
 */
export const WORKFLOW_PUBLIC_ROUTES: Routes = [
    // Backward-compatibility redirect: camelCase → kebab (email links use kebab, FOF-5501 regression)
    {
        path: 'accountRecoveryWorkflow',
        pathMatch: 'full',
        redirectTo: preserveParamsRedirect('/account-recovery-workflow')
    },

    // Account recovery workflow
    {
        path: 'account-recovery-workflow',
        loadComponent: () => import('src/components/account-recovery-workflow/account-recovery-workflow.component').then(m => m.AccountRecoveryWorkflowComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'accountRecoveryWorkflow',
            requiresPermission: false,
            unauthenticated: true
        } satisfies RouteData
    }
];
